import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/storefrontTheme.css';

export const COMMON_THEME_SECTION_TYPES = Object.freeze({
  PROMO: 'theme-promo',
  FAQ: 'theme-faq',
});

export const COMMON_THEME_SECTION_META = Object.freeze([
  { type: COMMON_THEME_SECTION_TYPES.PROMO, name: 'Promo banner', icon: 'megaphone', description: 'A focused campaign or limited-drop callout' },
  { type: COMMON_THEME_SECTION_TYPES.FAQ, name: 'FAQ', icon: 'quote', description: 'Answers to common purchasing questions' },
]);

export const isCommonThemeSection = (section) => COMMON_THEME_SECTION_META.some((item) => item.type === section?.type);

const defaultItems = [
  ['When will my order arrive?', 'You will receive tracking details as soon as your order ships.'],
  ['Can I return an item?', 'Returns are accepted according to the store return policy.'],
  ['Need more help?', 'Contact the store and we will be happy to help.'],
];

export function ThemeExtension({ section, preview = false }) {
  const content = section?.content || {};
  const settings = section?.settings || {};
  if (section?.type === COMMON_THEME_SECTION_TYPES.PROMO) {
    // A promo surface is deliberately allowed to be dark in a light palette.
    // It therefore owns an explicit foreground token rather than inheriting
    // the page background, which may itself be dark for a graphic theme.
    const promoTextColor = settings.textColor || '#f5f2eb';
    const promoSupportingTextColor = settings.supportingTextColor || promoTextColor;
    const promoSurface = {
      backgroundColor: settings.backgroundColor || 'var(--theme-secondary, #272622)',
      '--theme-extension-promo-copy': promoTextColor,
      '--theme-extension-promo-supporting-copy': promoSupportingTextColor,
    };
    const promoCopy = { color: 'var(--theme-extension-promo-copy)' };
    const promoSupportingCopy = { color: 'var(--theme-extension-promo-supporting-copy)', opacity: 0.78 };
    // Leave defaults to the stylesheet: it swaps the button to the dark
    // palette under the storefront appearance toggle. Explicit merchant
    // overrides remain stable across appearances by design.
    const promoButton = {
      ...(settings.buttonBackgroundColor ? { backgroundColor: settings.buttonBackgroundColor } : {}),
      ...(settings.buttonTextColor ? { color: settings.buttonTextColor } : {}),
    };

    return <section className={`theme-extension theme-extension--promo${preview ? ' theme-extension--preview' : ''}`} style={promoSurface}>
      <div className="theme-extension__inner">
        <div className="theme-extension__promo-copy">
          <span className="theme-extension__eyebrow" style={promoSupportingCopy}>{content.eyebrow || 'Just in'}</span>
          <h2 style={promoCopy}>{content.title || 'A little something worth seeing.'}</h2>
          {content.body && <p style={promoSupportingCopy}>{content.body}</p>}
        </div>
        {content.buttonText && (preview ? <span className="theme-extension__button" style={promoButton}>{content.buttonText} ↗</span> : <Link className="theme-extension__button" style={promoButton} to={content.buttonLink || '/shop'}>{content.buttonText} ↗</Link>)}
      </div>
    </section>;
  }
  const items = [0, 1, 2].map((index) => [content[`question${index + 1}`], content[`answer${index + 1}`]]).map(([question, answer], index) => [question || defaultItems[index][0], answer || defaultItems[index][1]]);
  return <section className={`theme-extension theme-extension--faq${preview ? ' theme-extension--preview' : ''}`}>
    <div className="theme-extension__inner">
      <span className="theme-extension__eyebrow">{content.eyebrow || 'Need to know'}</span>
      <h2>{content.title || 'A few helpful answers.'}</h2>
      <div className="theme-extension__faq-list">
        {items.map(([question, answer]) => <details key={question} open={preview}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}
      </div>
    </div>
  </section>;
}
